import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { CloseIcon } from '../common/CloseIcon';
import { Trans, useTranslation } from 'react-i18next';
import './CouldBuyOrCouldSellModal.scss'; // We'll create this stylesheet
import ExpanderArrow from './ExpanderArrow';
import { CSSTransition } from 'react-transition-group';
import OffMarketSellIcon from '../FeedEvents/OffMarketSellIcon';
import OffMarketBuyIcon from '../FeedEvents/OffMarketBuyIcon';
import { getCookie } from '../utils/utils';
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete';
import LocationIcon from '../FeedEvents/LocationIcon';
import mapboxgl from 'mapbox-gl';
import { fetchPOIsForBuilding } from '../AppContent/LandingPage.helpers';
import { saveEmptyBuildingPage } from './Report.fetchExistingReport';
import BuildingSelectionMap from './BuildingSelectionMap';
import { OffMarketToastMessage } from '../utils/types';
import SharePromptModal from '../common/SharePromptModal';
import { shareOffMarketWillingness, formatAddressString } from '../../utils/shareUtils';

type OffMarketAction = 'buy' | 'sell';

interface ImageData {
  id: string;
  url: string;
  thumbnailUrl: string;
  uploadedAt?: string;
  order?: number;
  isUploading?: boolean;
  uploadProgress?: number;
  error?: string;
}

export interface PropertyDetails {
  description?: string;
  size?: number;
  rooms?: string;
  floor?: number;
  hasElevator?: boolean;
  hasBalcony?: boolean;
  renovationStatus?: string;
  images?: ImageData[];
  address?: string;
  location?: {
    lat: number;
    lng: number;
  };
  buildingId?: string;
  buildingGeometry?: {
    type: string;
    coordinates: number[][][];
  };
}

type Props = {
  countryOfFeedContent: string;
  isOpen: boolean;
  onClose: () => void;
  actionType: OffMarketAction;
  isLoading: boolean;
  isContractor?: boolean;
  isLoggedIn?: boolean;
  setLoginModalVisible: (visible: boolean) => void;
  setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal: (message: OffMarketToastMessage | null) => void;
  onSelectBuilding?: (buildingId: string) => void;
  fetchConversations: () => void;
  fetchFeed: () => void;
};

const MAX_IMAGES = 30;

const CouldBuyOrCouldSellModal: React.FC<Props> = ({
  countryOfFeedContent,
  isOpen,
  onClose,
  actionType,
  isLoading,
  isContractor,
  isLoggedIn,
  setLoginModalVisible,
  setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal,
  onSelectBuilding,
  fetchConversations,
  fetchFeed
}) => {
  const { t } = useTranslation();
  const [description, setDescription] = useState<string>('');
  const [size, setSize] = useState<string>('');
  const [rooms, setRooms] = useState<string>('');
  const [floor, setFloor] = useState<string>('');
  const [hasElevator, setHasElevator] = useState<boolean>(false);
  const [hasBalcony, setHasBalcony] = useState<boolean>(false);
  const [renovationStatus, setRenovationStatus] = useState<string>('');
  const [isDetailsExpanded, setIsDetailsExpanded] = useState<boolean>(false);
  const [images, setImages] = useState<ImageData[]>([]);
  const [uploadError, setUploadError] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [buildingId, setBuildingId] = useState<string | null>(null);
  const [buildingGeometry, setBuildingGeometry] = useState<{ type: string; coordinates: number[][][] } | null>(null);
  const [isQueryingBuilding, setIsQueryingBuilding] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isAddressIncomplete, setIsAddressIncomplete] = useState<boolean>(false);
  const [addressResults, setAddressResults] = useState<any[]>([]);
  const [showBuildingSelectionMap, setShowBuildingSelectionMap] = useState<boolean>(false);
  const [addressMismatchError, setAddressMismatchError] = useState<boolean>(false);
  const [buildingAddress, setBuildingAddress] = useState<string>('');
  const [buildingIsSelected, setBuildingIsSelected] = useState<boolean>(false);
  const [showSharePrompt, setShowSharePrompt] = useState<boolean>(false);
  const [createdWillingnessData, setCreatedWillingnessData] = useState<{ eventId: string; buildingId: string; address: any; isMatch: boolean; } | null>(null);
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Function to get lat/lng from Mapbox instead of Google
  const getLatLngFromMapbox = async (address: string): Promise<{ lat: number; lng: number }> => {
    const encodedAddress = encodeURIComponent(address);
    const mapboxAccessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${mapboxAccessToken}&limit=1`
    );

    if (!response.ok) {
      throw new Error('Failed to geocode address with Mapbox');
    }

    const data = await response.json();

    if (!data.features || data.features.length === 0) {
      throw new Error('No results found for the address');
    }

    const [lng, lat] = data.features[0].center;
    return { lat, lng };
  };

  // Function to reverse geocode coordinates to get address
  const reverseGeocodeMapbox = async (lat: number, lng: number): Promise<string | null> => {
    const mapboxAccessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxAccessToken}&types=address&limit=1`
      );

      if (!response.ok) {
        throw new Error('Failed to reverse geocode with Mapbox');
      }

      const data = await response.json();

      if (!data.features || data.features.length === 0) {
        return null;
      }
      // Instead of returning the full place name, return an address string from the address object that 
      // does not include the postal code.
      // We'll construct the address from the context, omitting the postcode.
      const feature = data.features[0];
      if (!feature) return null;

      // Extract address components from the context array
      // Mapbox context types: https://docs.mapbox.com/api/search/geocoding/#response-object
      // We'll look for street, house number, city, country, but skip postcode
      let street = '';
      let houseNumber = '';
      let city = '';
      let country = '';

      // Sometimes address number is in the 'address' property
      if (feature.address) {
        houseNumber = feature.address;
      }

      // The 'text' property is usually the street name
      if (feature.text) {
        street = feature.text;
      }

      if (feature.context && Array.isArray(feature.context)) {
        for (const ctx of feature.context) {
          if (ctx.id.startsWith('place')) {
            city = ctx.text;
          }
          if (ctx.id.startsWith('country')) {
            country = ctx.text;
          }
          // Do not include postcode
        }
      }

      // Compose the address string, omitting empty parts and the postcode
      const addressParts = [];
      if (street) {
        addressParts.push(street + (houseNumber ? ' ' + houseNumber : ''));
      }
      if (city) {
        addressParts.push(city);
      }
      if (country) {
        addressParts.push(country);
      }

      return addressParts.join(', ') || feature.text;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  };

  // Helper function to normalize and compare addresses
  const addressesMatch = (address1: string, address2: string): boolean => {
    if (!address1 || !address2) return false;

    // Normalize addresses for comparison
    const normalize = (addr: string) => {
      return addr
        .toLowerCase()
        .replace(/[^\w\s]/g, '') // Remove punctuation
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();
    };

    const normalized1 = normalize(address1);
    const normalized2 = normalize(address2);

    // Check if one address contains the other (to handle cases where one is more detailed)
    return normalized1.includes(normalized2) || normalized2.includes(normalized1);
  };

  // Map country names to ISO country codes for Google Places API
  const countryToISOCode: { [key: string]: string } = {
    'Finland': 'fi',
    'finland': 'fi',
    'Sweden': 'se',
    'sweden': 'se',
    'Norway': 'no',
    'norway': 'no',
    'Denmark': 'dk',
    'denmark': 'dk',
    'Estonia': 'ee',
    'estonia': 'ee',
    'United States': 'us',
    'united states': 'us',
    'USA': 'us',
    'usa': 'us',
    'UK': 'gb',
    'uk': 'gb',
    'United Kingdom': 'gb',
    'united kingdom': 'gb',
    'Germany': 'de',
    'germany': 'de',
    'France': 'fr',
    'france': 'fr',
    'Spain': 'es',
    'spain': 'es',
    'Italy': 'it',
    'italy': 'it'
    // Add more country mappings as needed
  };

  // Get the ISO country code from countryOfFeedContent
  const getCountryCode = (country: string): string | undefined => {
    const normalized = country.toLowerCase().trim();
    return countryToISOCode[country] || countryToISOCode[normalized];
  };

  if (!isOpen) return null;

  // Function to validate if address has all required components using structured data
  const validateAddressFromResults = (results: any[]): boolean => {
    if (!results || results.length === 0) return false;

    const result = results[0];

    // Method 1: Check if Google considers this a complete street address
    if (result.types && result.types.includes('street_address')) {
      return true;
    }

    // Method 2: Check for essential components
    if (!result.address_components) return false;

    const components = result.address_components;
    const componentTypes = new Set<string>();

    // Collect all component types
    components.forEach((component: any) => {
      if (component.types && Array.isArray(component.types)) {
        component.types.forEach((type: string) => componentTypes.add(type));
      }
    });

    // Check for required components
    const hasStreetNumber = componentTypes.has('street_number');
    const hasRoute = componentTypes.has('route');
    const hasLocality = componentTypes.has('locality') ||
      componentTypes.has('postal_town') ||
      componentTypes.has('administrative_area_level_3') ||
      componentTypes.has('sublocality') ||
      componentTypes.has('sublocality_level_1');
    const hasCountry = componentTypes.has('country');

    // A complete address should have at least street number, route, some form of locality, and country
    return hasStreetNumber && hasRoute && hasLocality && hasCountry;
  };

  // Function to extract address components from Google Places results
  const extractAddressComponents = (results: any[]) => {
    if (!results || results.length === 0) {
      return null;
    }

    const result = results[0];
    const components = result.address_components || [];

    // Helper function to find component by type
    const findComponent = (types: string[]) => {
      for (const type of types) {
        const component = components.find((c: any) => c.types?.includes(type));
        if (component) return component;
      }
      return null;
    };

    // Extract components
    const streetNumberComponent = findComponent(['street_number']);
    const routeComponent = findComponent(['route']);
    const cityComponent = findComponent(['locality', 'postal_town', 'administrative_area_level_3', 'sublocality', 'sublocality_level_1']);
    const regionComponent = findComponent(['administrative_area_level_1', 'administrative_area_level_2']);
    const countryComponent = findComponent(['country']);
    const postalCodeComponent = findComponent(['postal_code']);

    return {
      streetNumber: streetNumberComponent?.long_name || '',
      street: routeComponent?.long_name || '',
      city: cityComponent?.long_name || '',
      region: regionComponent?.long_name || '',
      country: countryComponent?.long_name || '',
      countryCode: countryComponent?.short_name || '',
      postcode: postalCodeComponent?.long_name || '',
      // Full formatted address as backup
      formattedAddress: result.formatted_address || ''
    };
  };

  const onConfirm = async (propertyDetails?: PropertyDetails) => {
    if (!isLoggedIn) {
      setLoginModalVisible(true);
      return;
    }
    if (!address || !location) {
      alert(t('building.offMarket.modal.pleaseSelectAddress'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Extract address components from Google Places results
      const addressComponents = extractAddressComponents(addressResults);

      if (!addressComponents) {
        alert(t('building.offMarket.modal.invalidAddress'));
        return;
      }

      const addressObject = {
        country: addressComponents.country,
        region: addressComponents.region,
        city: addressComponents.city,
        postcode: addressComponents.postcode,
        street: addressComponents.street,
        houseNumber: addressComponents.streetNumber,
        coordinates: {
          lat: location.lat,
          lng: location.lng
        }
      };

      // Construct the request body
      const requestBody: any = {
        // Set could_sell or could_buy based on actionType
        [`could_${actionType}`]: true,
      };

      // Always include selected_building with address data
      if (buildingId && buildingGeometry) {
        // If we found a building, include all the building data
        requestBody.selected_building = {
          id: buildingId,
          centroid: location,
          geometry: buildingGeometry,
          addresses: [addressObject], // Backend expects addresses array at root level
          properties: {
            name: address,
            addresses: [addressObject], // Also include in properties for consistency
            type: "apartments"
          }
        };
      } else {
        // If no building found, still send basic structure with address
        requestBody.selected_building = {
          id: `temp_${Date.now()}`, // Temporary ID for buildings not in database
          centroid: location,
          geometry: { type: 'Polygon', coordinates: [] }, // Empty geometry
          addresses: [addressObject],
          properties: {
            name: address,
            addresses: [addressObject],
            type: "apartments"
          }
        };
      }

      // Add property details
      if (propertyDetails?.description) {
        requestBody.property_description = propertyDetails.description;
      }
      if (propertyDetails?.size) {
        requestBody.property_size = propertyDetails.size;
      }
      if (propertyDetails?.rooms) {
        requestBody.property_rooms = propertyDetails.rooms;
      }
      if (propertyDetails?.floor !== undefined) {
        requestBody.property_floor = propertyDetails.floor;
      }
      if (propertyDetails?.hasElevator !== undefined) {
        requestBody.property_has_elevator = propertyDetails.hasElevator;
      }
      if (propertyDetails?.hasBalcony !== undefined) {
        requestBody.property_has_balcony = propertyDetails.hasBalcony;
      }
      if (propertyDetails?.renovationStatus) {
        requestBody.property_renovation_status = propertyDetails.renovationStatus;
      }

      // Add images if present
      if (propertyDetails?.images && propertyDetails.images.length > 0) {
        requestBody.property_images = propertyDetails.images.map(img => ({
          id: img.id,
          url: img.url,
          thumbnailUrl: img.thumbnailUrl
        }));
      }

      // Add location data even if no building found
      requestBody.property_address = address;
      requestBody.property_location = location;

      // Send the request
      const response = await fetch(`${process.env.REACT_APP_API_URL}/off-market-willingness`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const buildingWithPOIs = await fetchPOIsForBuilding(
        {
          id: buildingId,
          centroid: location,
          geometry: buildingGeometry,
          addresses: [addressObject], // Backend expects addresses array at root level
          properties: {
            name: address,
            addresses: [addressObject], // Also include in properties for consistency
          },
          pois: []
        }
        , location);

      await saveEmptyBuildingPage({
        selectedBuilding: buildingWithPOIs
      });

      const responseData = await response.json();

      let isMatch = false;
      if (responseData.is_new_could_sell_and_buyers || responseData.is_new_could_buy_and_sellers) {
        isMatch = true;
      }

      // Store the created willingness data for sharing
      if (responseData.willingness_id) {
        setCreatedWillingnessData({
          eventId: responseData.willingness_id,
          buildingId: buildingId || responseData.building_id || '',
          address: addressObject,
          isMatch: isMatch,
        });
      }

      // Set the state for toast messages
      if (responseData.is_new_could_sell_and_buyers) {
        setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal('COULD_SELL_AND_BUYERS');
      } else if (responseData.is_new_could_buy_and_sellers) {
        setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal('COULD_BUY_AND_SELLERS');
      } else {
        setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal(actionType === 'sell' ? 'COULD_SELL_NO_BUYERS' : 'COULD_BUY_NO_SELLERS');
      }

      // Show share prompt (keep modal open)
      setShowSharePrompt(true);

      // Navigation will happen after share prompt is dismissed

      if (responseData.is_new_could_sell_and_buyers || responseData.is_new_could_buy_and_sellers) {
        fetchConversations();
      }
      fetchFeed();

    } catch (error) {
      console.error('Error submitting off-market willingness:', error);
      alert(t('landingPage.couldBuyOrCouldSellModal.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    const propertyDetails: PropertyDetails = {};

    if (description) propertyDetails.description = description;
    if (size) propertyDetails.size = Number(size);
    if (rooms) propertyDetails.rooms = rooms;
    if (floor) propertyDetails.floor = Number(floor);
    propertyDetails.hasElevator = hasElevator;
    propertyDetails.hasBalcony = hasBalcony;
    if (renovationStatus) propertyDetails.renovationStatus = renovationStatus;
    if (address) propertyDetails.address = address;
    if (location) propertyDetails.location = location;
    if (buildingId) propertyDetails.buildingId = buildingId;
    if (buildingGeometry) propertyDetails.buildingGeometry = buildingGeometry;

    // Only include successfully uploaded images (those that have an id and no error)
    const validImages = images.filter(img => img.id && !img.error && !img.isUploading);
    if (validImages.length > 0) {
      propertyDetails.images = validImages;
    }

    // Call onConfirm and wait for it to complete
    await onConfirm(propertyDetails);

    // Reset form only after successful submission
    if (isLoggedIn) {
      setDescription('');
      setSize('');
      setRooms('');
      setFloor('');
      setHasElevator(false);
      setHasBalcony(false);
      setRenovationStatus('');
      setImages([]);
      setUploadError('');
      setAddress('');
      setLocation(null);
      setBuildingId(null);
      setBuildingGeometry(null);
      setAddressResults([]);
      setBuildingAddress('');
      setBuildingIsSelected(false);
    }
  };

  const resetForm = () => {
    setDescription('');
    setSize('');
    setRooms('');
    setFloor('');
    setHasElevator(false);
    setHasBalcony(false);
    setRenovationStatus('');
    setIsDetailsExpanded(false);
    setImages([]);
    setUploadError('');
    setAddress('');
    setLocation(null);
    setBuildingId(null);
    setBuildingGeometry(null);
    setIsAddressIncomplete(false);
    setAddressResults([]);
    setShowBuildingSelectionMap(false);
    setAddressMismatchError(false);
    setBuildingAddress('');
    setBuildingIsSelected(false);
    setShowSharePrompt(false);
    setCreatedWillingnessData(null);
    setIsSharing(false);
    onClose();
  };

  const handleShareWillingness = async () => {
    if (!createdWillingnessData) return;

    setIsSharing(true);
    await shareOffMarketWillingness({
      buildingId: createdWillingnessData.buildingId,
      eventId: createdWillingnessData.eventId,
      isSellInterest: actionType === 'sell',
      address: createdWillingnessData.address,
      t: t,
      isMatch: createdWillingnessData.isMatch
    });

    setIsSharing(false);
    
    // Always close after sharing attempt (whether successful or not)
    handleCloseSharePrompt();
  };

  const handleCloseSharePrompt = () => {
    setShowSharePrompt(false);
    
    // Navigate to building page if we have a building ID
    if (createdWillingnessData?.buildingId && onSelectBuilding) {
      onSelectBuilding(createdWillingnessData.buildingId);
    }
    
    // Clear data and close the main modal
    setCreatedWillingnessData(null);
    onClose();
  };

  const toggleDetailsExpansion = () => {
    setIsDetailsExpanded(!isDetailsExpanded);
  };

  const handleBuildingSelect = async (buildingId: string | null, buildingGeometry: any | null, userClickedCoordinates: { lat: number; lng: number } | null) => {
    setBuildingId(buildingId);
    setBuildingGeometry(buildingGeometry);
    setBuildingIsSelected(true);
    
    // If a building was selected and we have location and address, verify the address matches
    if (buildingId && buildingGeometry && location && address) {
      const buildingAddress = await reverseGeocodeMapbox(userClickedCoordinates.lat, userClickedCoordinates.lng);
      setBuildingAddress(buildingAddress);
      if (buildingAddress) {
        const matches = addressesMatch(address, buildingAddress);
        
        if (!matches) {
          console.warn('Address mismatch detected for manually selected building:', {
            userAddress: address,
            buildingAddress: buildingAddress
          });
          setAddressMismatchError(true);
          // Don't clear the selection here - let the user see the error but keep their selection
        } else {
          setAddressMismatchError(false);
        }
      }
    } else if (!buildingId) {
      // Building was deselected
      setAddressMismatchError(false);
    }
  };

  const handleAddressChange = (address: string) => {
    setAddress(address);
    // Reset states when user types
    setIsAddressIncomplete(false);
    setLocation(null);
    setBuildingId(null);
    setBuildingGeometry(null);
    setAddressResults([]);
    setShowBuildingSelectionMap(false);
    setAddressMismatchError(false);
  };

  const queryBuildingAtLocation = async (lat: number, lng: number, userAddress: string): Promise<{ id: string | null; geometry: any | null }> => {
    return new Promise((resolve) => {
      // Set Mapbox access token
      mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

      // Create a temporary hidden map container
      const tempContainer = document.createElement('div');
      tempContainer.style.width = '100px';
      tempContainer.style.height = '100px';
      tempContainer.style.visibility = 'hidden';
      tempContainer.style.position = 'absolute';
      document.body.appendChild(tempContainer);

      // Create a temporary map instance
      const tempMap = new mapboxgl.Map({
        container: tempContainer,
        style: `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`,
        center: [lng, lat],
        zoom: 17, // High zoom level to ensure building features are loaded
        interactive: false
      });

      tempMap.on('load', async () => {
        // Query for building features at the location
        const point = tempMap.project([lng, lat]);
        const features = tempMap.queryRenderedFeatures(point, {
          layers: ['building', 'test-new-building'] // Same layers as in BuildingMap
        });

        let buildingId: string | null = null;
        let buildingGeometry: any | null = null;

        if (features.length > 0) {
          const buildingFeature = features[0];

          // Get building ID
          buildingId = buildingFeature.id?.toString() || null;

          // Get building geometry if it's a polygon
          if (buildingFeature.geometry?.type === 'Polygon') {
            buildingGeometry = {
              type: buildingFeature.geometry.type,
              coordinates: buildingFeature.geometry.coordinates
            };

            // Perform reverse geocoding to verify the address matches
            const buildingAddress = await reverseGeocodeMapbox(lat, lng);
            
            if (buildingAddress && userAddress) {
              // Check if the addresses match
              const matches = addressesMatch(userAddress, buildingAddress);
              
              if (!matches) {
                console.warn('Address mismatch detected:', {
                  userAddress: userAddress,
                  buildingAddress: buildingAddress
                });
                setAddressMismatchError(true);
                // Clear the building selection if addresses don't match
                buildingId = null;
                buildingGeometry = null;
              } else {
                setAddressMismatchError(false);
              }
            }
          }
        }

        // Clean up
        tempMap.remove();
        document.body.removeChild(tempContainer);

        resolve({ id: buildingId, geometry: buildingGeometry });
      });

      tempMap.on('error', (error) => {
        console.error('Error loading temporary map:', error);
        // Clean up on error
        tempMap.remove();
        document.body.removeChild(tempContainer);
        resolve({ id: null, geometry: null });
      });
    });
  };

  const handleAddressSelect = async (address: string) => {
    try {
      const results = await geocodeByAddress(address);
      if (results.length === 0) return;

      setAddress(address);
      setAddressResults(results); // Save results for later use

      // Validate address completeness using structured results data
      const isAddressValid = validateAddressFromResults(results);
      setIsAddressIncomplete(!isAddressValid);

      if (!isAddressValid) {
        // Don't query for building if address is incomplete
        setLocation(null);
        setBuildingId(null);
        setBuildingGeometry(null);
        setShowBuildingSelectionMap(false);
        return;
      }

      // Use Mapbox API to get lat/lng instead of Google's getLatLng
      const latLng = await getLatLngFromMapbox(address);

      setLocation(latLng);

      // Show the building selection map for user to click on the correct building
      setShowBuildingSelectionMap(true);

      // Auto-query for building at this location as a fallback
      setIsQueryingBuilding(true);

      // Query Mapbox for building features at this location
      const { id, geometry } = await queryBuildingAtLocation(latLng.lat, latLng.lng, address);

      if (id) {
        setBuildingId(id);
        setBuildingGeometry(geometry);
        console.log('Auto-detected building ID:', id);
      } else {
        setBuildingId(null);
        setBuildingGeometry(null);
        console.log('No building auto-detected, user should click on map');
      }

      setIsQueryingBuilding(false);

    } catch (error) {
      console.error('Error selecting address:', error);
      setIsQueryingBuilding(false);
    }
  };

  const handleImageUpload = async (files: FileList) => {
    // Convert FileList to array
    const fileArray = Array.from(files);

    // Check if adding new files would exceed the limit
    if (images.length + fileArray.length > MAX_IMAGES) {
      setUploadError(t('building.offMarket.modal.imageLimitExceeded', { max: MAX_IMAGES }));
      return;
    }

    // Filter only image files
    const imageFiles = fileArray.filter(file => file.type.startsWith('image/'));

    // Create temporary image entries with upload status
    const newImagesWithStatus = imageFiles.map(file => ({
      id: '', // Will be assigned after upload
      url: URL.createObjectURL(file), // Create temporary URL for preview
      thumbnailUrl: URL.createObjectURL(file),
      isUploading: true,
      uploadProgress: 0,
      error: undefined
    }));

    // Add to images state
    setImages(prev => [...prev, ...newImagesWithStatus]);

    // Upload each image
    imageFiles.forEach(async (file, index) => {
      const formData = new FormData();
      formData.append('image', file);

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/upload-property-image`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            // No Content-Type header - browser will set it with boundary for FormData
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }

        const data = await response.json();

        // Update image data with info from server
        setImages(prev => {
          const newImages = [...prev];
          const targetIndex = prev.length - imageFiles.length + index;

          if (newImages[targetIndex]) {
            newImages[targetIndex] = {
              id: data.id,
              url: data.url,
              thumbnailUrl: data.thumbnailUrl,
              isUploading: false,
              uploadProgress: 100
            };
          }

          return newImages;
        });

        // Revoke object URL to avoid memory leaks
        URL.revokeObjectURL(newImagesWithStatus[index].url);
        URL.revokeObjectURL(newImagesWithStatus[index].thumbnailUrl);

      } catch (error) {
        // Update image with error status
        setImages(prev => {
          const newImages = [...prev];
          const targetIndex = prev.length - imageFiles.length + index;

          if (newImages[targetIndex]) {
            newImages[targetIndex] = {
              ...newImages[targetIndex],
              isUploading: false,
              error: error instanceof Error ? error.message : String(error)
            };
          }

          return newImages;
        });

        // Revoke object URL to avoid memory leaks
        URL.revokeObjectURL(newImagesWithStatus[index].url);
        URL.revokeObjectURL(newImagesWithStatus[index].thumbnailUrl);
      }
    });

    setUploadError('');
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleImageUpload(e.target.files);
      // Reset the input value so the same file can be selected again
      e.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImageUpload(e.dataTransfer.files);
    }
  };

  const removeImage = (index: number) => {
    const imageToRemove = images[index];

    // If the image has temporary URLs (from createObjectURL), revoke them
    if (imageToRemove.isUploading || imageToRemove.error) {
      URL.revokeObjectURL(imageToRemove.url);
      URL.revokeObjectURL(imageToRemove.thumbnailUrl);
    }

    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Use createPortal to render outside the parent component hierarchy
  return (
    <>
      {ReactDOM.createPortal(
    <div className="could-buy-or-could-sell-modal-overlay" style={{ display: showSharePrompt ? 'none' : 'flex' }}>
      <div className="could-buy-or-could-sell-modal">
        <div className="could-buy-or-could-sell-modal__close-button-wrapper">
          <button
            className="could-buy-or-could-sell-modal__close-button"
            onClick={resetForm}
            disabled={isLoading}
          >
            <CloseIcon size={20} />
          </button>
        </div>
        <div className="could-buy-or-could-sell-modal__header">
          <h2 className="could-buy-or-could-sell-modal__title">
            {actionType === 'sell' ? (
              <OffMarketSellIcon
                width="26px"
                height="26px"
                className="could-buy-or-could-sell-modal__sell-icon"
              />
            ) : (
              <OffMarketBuyIcon
                width="26px"
                height="26px"
                className="could-buy-or-could-sell-modal__buy-icon"
              />
            )}
            <Trans
              i18nKey={`landingPage.couldBuyOrCouldSellModal.${actionType}.title`}
            />
          </h2>
        </div>

        <div className="could-buy-or-could-sell-modal__content">
          <p className="could-buy-or-could-sell-modal__description">
            {isContractor ?
              <Trans
                i18nKey={`building.offMarket.modal.contractor.buyDescription`}
              />
              :
              <Trans
                i18nKey={`landingPage.couldBuyOrCouldSellModal.${actionType}.description`}
              />
            }
          </p>

          <div className="could-buy-or-could-sell-modal__form-field">
            <label htmlFor="property-address">
              <Trans i18nKey="building.offMarket.modal.address" />
              <span className="could-buy-or-could-sell-modal__location-icon">
                <LocationIcon
                  isCouldBuyOrCouldSellModal
                />
              </span>
            </label>
            <PlacesAutocomplete
              value={address}
              onChange={handleAddressChange}
              onSelect={handleAddressSelect}
              searchOptions={{
                componentRestrictions: {
                  country: getCountryCode(countryOfFeedContent) || undefined
                }
              }}
            >
              {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div className="could-buy-or-could-sell-modal__autocomplete">
                  <input
                    {...getInputProps({
                      placeholder: t('building.offMarket.modal.addressPlaceholder'),
                      className: 'could-buy-or-could-sell-modal__input',
                      id: 'property-address',
                      disabled: isQueryingBuilding
                    })}
                  />
                  {suggestions.length > 0 && (
                    <div className="could-buy-or-could-sell-modal__suggestions">
                      {loading && (
                        <div className="could-buy-or-could-sell-modal__suggestion could-buy-or-could-sell-modal__suggestion--loading">
                          {t('building.offMarket.modal.loading')}
                        </div>
                      )}
                      {suggestions.map((suggestion) => (
                        <div
                          {...getSuggestionItemProps(suggestion, {
                            className: `could-buy-or-could-sell-modal__suggestion ${suggestion.active ? 'could-buy-or-could-sell-modal__suggestion--active' : ''
                              }`
                          })}
                          key={suggestion.placeId}
                        >
                          <span>{suggestion.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </PlacesAutocomplete>
          </div>

          {/* Building Selection Map */}
          {location && (
            <BuildingSelectionMap
              location={location}
              onBuildingSelect={handleBuildingSelect}
              isVisible={showBuildingSelectionMap}
              isQueryingBuilding={isQueryingBuilding}
              address={address}
              buildingId={buildingId}
              showBuildingSelectionMap={showBuildingSelectionMap}
              isAddressIncomplete={isAddressIncomplete}
              addressMismatchError={addressMismatchError}
              actionType={actionType}
              buildingAddress={buildingAddress} 
            />
          )}

          <div className="could-buy-or-could-sell-modal__form">
            <div className="could-buy-or-could-sell-modal__expander">
              <button
                onClick={toggleDetailsExpansion}
                className="could-buy-or-could-sell-modal__expander-button"
              >
                <Trans i18nKey="building.offMarket.modal.additionalDetails" />
                <ExpanderArrow isExpanded={isDetailsExpanded} />
              </button>
            </div>

            <CSSTransition
              in={isDetailsExpanded}
              timeout={300}
              classNames="details-expand"
              unmountOnExit
            >
              <div className="could-buy-or-could-sell-modal__additional-details">

                <div className="could-buy-or-could-sell-modal__form-field">
                  <label htmlFor="property-description">
                    <Trans i18nKey="building.offMarket.modal.description" />
                  </label>
                  <textarea
                    id="property-description"
                    className="could-buy-or-could-sell-modal__textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={actionType === 'buy' ? t('building.offMarket.modal.buyingInterestDescriptionPlaceholder') : t('building.offMarket.modal.sellingInterestDescriptionPlaceholder')}
                    rows={3}
                    maxLength={450}
                  />
                  <span className="could-buy-or-could-sell-modal__textarea-counter">{description.length} / 450</span>
                </div>
                <div className="could-buy-or-could-sell-modal__form-row">
                  <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--half">
                    <label htmlFor="property-size">
                      {actionType === 'buy' ? <Trans i18nKey="building.offMarket.modal.sizeClass" /> : <Trans i18nKey="building.offMarket.modal.size" />}
                    </label>
                    <input
                      id="property-size"
                      type="number"
                      className="could-buy-or-could-sell-modal__input"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      placeholder={t('building.offMarket.modal.sizePlaceholder')}
                      min="0"
                    />
                  </div>

                  <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--half">
                    <label htmlFor="property-rooms">
                      <Trans i18nKey="building.offMarket.modal.roomsLabel" />
                    </label>
                    <select
                      id="property-rooms"
                      className="could-buy-or-could-sell-modal__select"
                      value={rooms}
                      onChange={(e) => setRooms(e.target.value)}
                    >
                      <option value="">
                        {t('building.offMarket.modal.selectRooms')}
                      </option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5+">5+</option>
                    </select>
                  </div>
                </div>
                {!isContractor && (
                  <>
                    <div className="could-buy-or-could-sell-modal__form-row">
                      <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--half">
                        <label htmlFor="property-floor">
                          <Trans i18nKey="building.offMarket.modal.floor" />
                        </label>
                        <input
                          id="property-floor"
                          type="number"
                          className="could-buy-or-could-sell-modal__input"
                          value={floor}
                          onChange={(e) => setFloor(e.target.value)}
                          placeholder={t('building.offMarket.modal.floorPlaceholder')}
                          min="0"
                        />
                      </div>

                      <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--half">
                        <label htmlFor="property-renovation">
                          <Trans i18nKey="building.offMarket.modal.renovation" />
                        </label>
                        <select
                          id="property-renovation"
                          className="could-buy-or-could-sell-modal__select"
                          value={renovationStatus}
                          onChange={(e) => setRenovationStatus(e.target.value)}
                        >
                          <option value="">
                            {t('building.offMarket.modal.selectRenovation')}
                          </option>
                          <option value="recently_renovated">
                            {t('building.offMarket.modal.recentlyRenovated')}
                          </option>
                          <option value="good_condition">
                            {t('building.offMarket.modal.goodCondition')}
                          </option>
                          <option value="needs_renovation">
                            {t('building.offMarket.modal.needsRenovation')}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div className="could-buy-or-could-sell-modal__form-row could-buy-or-could-sell-modal__form-row--checkboxes">
                      <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--checkbox">
                        <input
                          id="property-elevator"
                          type="checkbox"
                          checked={hasElevator}
                          onChange={(e) => setHasElevator(e.target.checked)}
                        />
                        <label htmlFor="property-elevator">
                          <Trans i18nKey="building.offMarket.modal.hasElevator" />
                        </label>
                      </div>

                      <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--checkbox">
                        <input
                          id="property-balcony"
                          type="checkbox"
                          checked={hasBalcony}
                          onChange={(e) => setHasBalcony(e.target.checked)}
                        />
                        <label htmlFor="property-balcony">
                          <Trans i18nKey="building.offMarket.modal.hasBalcony" />
                        </label>
                      </div>
                    </div>

                    {/* Image Upload Section */}
                    {actionType === 'sell' && (
                      <div
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={triggerFileInput}
                        className="could-buy-or-could-sell-modal__image-upload-section">
                        <h4 className="could-buy-or-could-sell-modal__image-upload-title">
                          <Trans i18nKey="building.offMarket.modal.addPropertyImages" />
                          <span className="could-buy-or-could-sell-modal__image-count">
                            {images.length}/{MAX_IMAGES}
                          </span>
                        </h4>

                        <div
                          className="could-buy-or-could-sell-modal__image-dropzone"
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                            className="could-buy-or-could-sell-modal__file-input"
                            onChange={handleFileInputChange}
                          />
                          <div className="could-buy-or-could-sell-modal__dropzone-content">
                            <div className="could-buy-or-could-sell-modal__upload-icon">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 5V19M12 5L7 10M12 5L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <p className="could-buy-or-could-sell-modal__dropzone-text">
                              <Trans i18nKey="building.offMarket.modal.dragImagesHere" />
                            </p>
                          </div>
                        </div>

                        {uploadError && (
                          <div className="could-buy-or-could-sell-modal__upload-error">
                            {uploadError}
                          </div>
                        )}

                        {images.length > 0 && (
                          <div className="could-buy-or-could-sell-modal__image-preview-grid">
                            {images.map((image, index) => (
                              <div
                                key={index}
                                className={`could-buy-or-could-sell-modal__image-preview ${image.isUploading ? 'is-uploading' : ''} ${image.error ? 'has-error' : ''}`}
                              >
                                <img
                                  src={image.thumbnailUrl}
                                  alt={t('building.offMarket.modal.propertyImage')}
                                  className="could-buy-or-could-sell-modal__preview-img"
                                />

                                <button
                                  className="could-buy-or-could-sell-modal__image-remove"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeImage(index);
                                  }}
                                  title={t('building.offMarket.modal.removeImage')}
                                >
                                  <CloseIcon size={14} />
                                </button>

                                {image.isUploading && (
                                  <div className="could-buy-or-could-sell-modal__image-upload-progress">
                                    <div className="could-buy-or-could-sell-modal__progress-indicator"></div>
                                  </div>
                                )}

                                {image.error && (
                                  <div className="could-buy-or-could-sell-modal__image-error-overlay">
                                    <span className="could-buy-or-could-sell-modal__image-error-text">
                                      {t('building.offMarket.modal.uploadFailed')}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>)
                }
              </div>
            </CSSTransition>
          </div>
        </div>

        <div className="could-buy-or-could-sell-modal__footer">
          <button
            className="could-buy-or-could-sell-modal__button could-buy-or-could-sell-modal__button--cancel"
            onClick={resetForm}
            disabled={isLoading || isSubmitting}
          >
            <Trans i18nKey="building.offMarket.modal.cancel" />
          </button>
          <button
            className="could-buy-or-could-sell-modal__button could-buy-or-could-sell-modal__button--confirm"
            onClick={handleSubmit}
            disabled={isLoading || isSubmitting || images.some(img => img.isUploading) || !address || isAddressIncomplete || !buildingId || addressMismatchError || !buildingIsSelected}
          >
            {isSubmitting ? (
              <div className="could-buy-or-could-sell-modal__button-spinner"></div>
            ) : (
              <Trans i18nKey="building.offMarket.modal.confirm" />
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body // Render directly to the body element
  )}
      <SharePromptModal
        isOpen={showSharePrompt}
        onClose={handleCloseSharePrompt}
        onShare={handleShareWillingness}
        actionType={actionType}
        buildingAddress={createdWillingnessData ? formatAddressString(createdWillingnessData.address) : undefined}
        isSharing={isSharing}
        isMatch={createdWillingnessData ? createdWillingnessData.isMatch : false}
      />
    </>
  );
};

export default CouldBuyOrCouldSellModal;